import { SearchFilterAccordion } from '@components/SearchFilterAccordion'
import { SearchFilterFacet } from '@components/SearchFilterFacet'
import { useBookSearch, useBookSearchFacet, useBookSearchSortBy } from '@hermes/search'
import { TranslationKey } from '@hermes/translations'
import {
  BookIcon,
  Box,
  Chip,
  FlagIcon,
  InfoCircle,
  List,
  PackageIcon,
  PenIcon,
  PlanetIcon,
  PlayMovieIcon,
  Stack,
  Typography,
  useTheme
} from '@hermes/web-components'
import { useDebounce } from '@hooks/useDebounce'
import { useNextLocalStorage } from '@hooks/useNextLocalStorage'
import useTranslateMessage from '@hooks/useTranslateMessage'
import { useAnalytics } from '@lib/contexts/analyticsContext'
import { useEffect, useRef, useState } from 'react'
import BaseModal from './BaseModal'
import { ToggleWithControl } from './ToggleWithControl'

const SemanticSearchInfoModal = ({
  modalOpen,
  setModalOpen
}: {
  modalOpen: boolean
  setModalOpen: (arg: boolean) => void
}) => {
  const { setQuery, setSemanticEnabled } = useBookSearch()
  const { resetSorting } = useBookSearchSortBy()
  const { setRefinement } = useBookSearchFacet({ attribute: 'books.availability.shipping.available' })

  const { t } = useTranslateMessage()

  const suggestions = [
    `${t({ id: 'search.suggestion.1', defaultMessage: 'Bok for min 5 år gamle dyreelskende datter' })}`,
    `${t({ id: 'search.suggestion.3', defaultMessage: 'Koselig roman som har en kvinnelig hovedrolle' })}`,
    `${t({ id: 'search.suggestion.2', defaultMessage: 'Motiverende bok for unge idrettsutøvere' })}`,
    `${t({ id: 'search.suggestion.4', defaultMessage: 'Trenger hjelp til å forstå en barnslig tenåring' })}`
  ]

  const trimEmojis = (value: string) => value.replace(/[\u{1F300}-\u{1F9FF}]/gu, '').trim()

  const handleChipClick = (value: string) => {
    setQuery(trimEmojis(value))
    setModalOpen(false)
    setSemanticEnabled(true)
    resetSorting()
    setRefinement('true')
  }

  return (
    <BaseModal
      isOpen={modalOpen}
      onClose={() => setModalOpen(false)}
      width={423}
      title={t({ id: 'semanticSearchInfoModal.title', defaultMessage: '🎉 Nyhet! Søk med naturlig språk' })}
    >
      <Box sx={{ padding: 3, maxWidth: 423, paddingBottom: 5, width: '100%', overflow: 'auto' }}>
        <Typography variant="body1">
          {t({
            id: 'semanticSearchInfoModal.ai-search-description.1',
            defaultMessage:
              'Finn det du leter etter ved å bruke et naturlig språk, slik som vist i eksemplene under. Du kan når som helst slå av/på "Søk med KI" med knappen i sidefeltet.'
          })}
        </Typography>
        <Typography variant="body1">
          <br />
          {t({
            id: 'semanticSearchInfoModal.ai-search-description.2',
            defaultMessage: 'Klikk på en av disse for å prøve selv:'
          })}
        </Typography>
        <Stack direction="column" spacing={1} mt={2}>
          {suggestions.map((suggestion, index) => (
            <Chip
              key={index}
              label={suggestion}
              onClick={() => handleChipClick(suggestion)}
              variant="outlined"
              sx={{
                whiteSpace: 'normal',
                wordBreak: 'break-word',
                cursor: 'pointer'
              }}
            />
          ))}
        </Stack>
        <Typography variant="body1">
          <br />
          {t({
            id: 'semanticSearchInfoModal.ai-search-description.3',
            defaultMessage: 'Vi vil kontinuerlige forbedre søkefunksjonen fremover, og vi hører gjerne fra deg!'
          })}
        </Typography>
      </Box>
    </BaseModal>
  )
}

const CUTOFF_DATE_AUTO_SHOW_MODAL = new Date('2025-04-01')

export const SearchFacets = () => {
  const { t } = useTranslateMessage()
  const { semanticEnabled, setSemanticEnabled } = useBookSearch()
  const { value: hasSeenModal, setValue: setHasSeenModal } = useNextLocalStorage('hasSeenSemanticSearchModal', false)
  const [modalOpen, setModalOpen] = useState(false)
  const hasSeenModalDebounced = useDebounce(hasSeenModal, 5000, true)
  const theme = useTheme()
  const analytics = useAnalytics()
  const isInitialMount = useRef(true)
  const handleModalClose = () => {
    setModalOpen(false)
    setHasSeenModal(true)
  }

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false
      return
    }
    analytics.trackEvent('SEARCH_SEMANTIC_TOGGLED', {
      enabled: semanticEnabled
    })
  }, [semanticEnabled])

  useEffect(() => {
    if (!hasSeenModalDebounced && new Date() < CUTOFF_DATE_AUTO_SHOW_MODAL) {
      setModalOpen(true)
    }
  }, [hasSeenModalDebounced])

  return (
    <Stack sx={{ minWidth: '300px' }}>
      <SemanticSearchInfoModal modalOpen={modalOpen} setModalOpen={handleModalClose} />
      <List>
        <ToggleWithControl
          toggled={semanticEnabled}
          toggleTitle={t({ id: 'search.semantic-search', defaultMessage: 'Søk med KI' })}
          setToggled={setSemanticEnabled}
          infoProps={{
            title: t({ id: 'search.semantic-search-info', defaultMessage: 'Nyhet 🎉' }),
            action: () => setModalOpen(true)
          }}
        >
          <Box flexGrow={1} display="flex" alignItems="center">
            <InfoCircle
              onClick={() => setModalOpen(true)}
              width="14px"
              height="14px"
              fill={theme.palette.neutral.main}
              style={{ cursor: 'pointer' }}
            />
          </Box>
        </ToggleWithControl>
        <SearchFilterAccordion
          icon={<PackageIcon />}
          defaultExpanded
          title={`${t({ id: 'search.available', defaultMessage: 'Available' })}`}
        >
          <SearchFilterFacet
            title={'shipping'}
            attribute="books.availability.shipping.available"
            operator="and"
            transformItems={(items) =>
              items
                .filter((item) => item.value === 'true')
                .map((item) => ({
                  ...item,
                  label: t({ id: 'books.availability.shipping.available' })
                }))
            }
          />
          <SearchFilterFacet
            title={'meetup'}
            attribute="books.availability.meetup.available"
            operator="and"
            shouldShowEmptyState={false}
            transformItems={(items) =>
              items
                .filter((item) => item.value === 'true')
                .map((item) => ({
                  ...item,
                  label: t({ id: 'books.availability.meetup.available' })
                }))
            }
          />
        </SearchFilterAccordion>
        <SearchFilterAccordion icon={<BookIcon />} title={`${t({ id: 'format', defaultMessage: 'Format' })}`}>
          <SearchFilterFacet
            attribute={'books.format'}
            title={`${t({ id: 'format', defaultMessage: 'Format' })}`}
            operator="and"
            sortBy={'value'}
            transformItems={(items) =>
              items.map((item) => ({
                ...item,
                label: t({ id: item.value as TranslationKey })
              }))
            }
          />
        </SearchFilterAccordion>
        <SearchFilterAccordion icon={<FlagIcon />} title={`${t({ id: 'language', defaultMessage: 'Languages' })}`}>
          <SearchFilterFacet
            attribute={'books.languages'}
            sortBy={'count'}
            title={`${t({ id: 'language', defaultMessage: 'Languages' })}`}
            operator="and"
            transformItems={(items) =>
              items.map((item) => ({
                ...item,
                label: t({ id: `${item.value}` as TranslationKey })
              }))
            }
          />
        </SearchFilterAccordion>
        <SearchFilterAccordion icon={<PlanetIcon />} title={`${t({ id: 'categories', defaultMessage: 'Categories' })}`}>
          <SearchFilterFacet
            attribute={'genres.code'}
            sortBy={'count'}
            title={`${t({ id: 'categories', defaultMessage: 'Categories' })}`}
            operator="and"
            limit={10}
            transformItems={(items) =>
              items.map((item) => ({
                ...item,
                label: t({ id: `genre.thema.${item.value}` as TranslationKey })
              }))
            }
          />
        </SearchFilterAccordion>
        <SearchFilterAccordion icon={<PenIcon />} title={`${t({ id: 'search.authors', defaultMessage: 'Authors' })}`}>
          <SearchFilterFacet
            attribute={'authors.primaryRoles.fullName'}
            title={`${t({ id: 'authors', defaultMessage: 'Authors' })}`}
            operator="or"
            sortBy={'count'}
            limit={20}
            transformItems={(items) =>
              items.map((item) => ({
                ...item,
                label: item.value
              }))
            }
          />
        </SearchFilterAccordion>
        <SearchFilterAccordion
          icon={<PlayMovieIcon />}
          title={`${t({ id: 'search.series', defaultMessage: 'Series' })}`}
        >
          <SearchFilterFacet
            hasSearchField={false}
            attribute={'seriesTitle'}
            title={'SeriesTitle'}
            operator="and"
            limit={20}
          />
        </SearchFilterAccordion>
      </List>
    </Stack>
  )
}
